import React, { useEffect, useState, useCallback } from 'react';
import { Star, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';
import { googleReviewsApi } from '../lib/api';

/**
 * Pannello recensioni Google — rating + count + 5 review testuali.
 *
 * Tipografia allineata alle convenzioni della Store Dashboard:
 *   - `font-heading` + `text-2xl md:text-4xl font-bold tabular-nums` per
 *     i numeri hero (rating).
 *   - `text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400`
 *     per i micro-label sopra le metriche.
 *   - `text-xs uppercase tracking-wider font-semibold` per le action
 *     button (Aggiorna / Maps), stesso stile di "Anno precedente" sulla
 *     riga del periodo.
 *   - Niente padding eccessivo, niente bordi colorati: tutto in slate
 *     come il resto della pagina.
 */
export default function GoogleReviewsPanel({ storeId, placeId, canRefresh = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await googleReviewsApi.store(storeId);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      await googleReviewsApi.refreshStore(storeId);
      await loadReviews();
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadReviews(); }, [loadReviews]);

  if (!placeId) {
    return (
      <div className="bg-white border border-slate-200 p-6 text-center" data-testid="google-reviews-no-pid">
        <AlertCircle className="w-6 h-6 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-600 font-medium">Place ID Google non configurato</p>
        <p className="text-xs text-slate-400 mt-1">Aggiungilo da Impostazioni → Google Reviews per attivare il sync</p>
      </div>
    );
  }
  if (loading) {
    return <div className="bg-white border border-slate-200 p-6 text-sm text-slate-400">Caricamento recensioni…</div>;
  }
  if (error) {
    return (
      <div className="bg-white border border-red-200 p-6 text-sm text-red-700" data-testid="google-reviews-error">
        Errore: {error}
      </div>
    );
  }

  const rating = data?.rating;
  const reviewCount = data?.user_rating_count;
  const reviews = data?.reviews || [];
  const stale = data?.stale;
  const displayName = data?.display_name;
  // Formato ufficiale "cross-platform" delle Google Maps URLs API: funziona
  // identico su browser e su app mobile (query_place_id risolve il place,
  // a differenza di /maps/place/?q=place_id:... che l'app mobile interpreta
  // come testo di ricerca letterale).
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayName || 'Google')}&query_place_id=${encodeURIComponent(placeId)}`;

  return (
    <div className="bg-white border border-slate-200" data-testid="google-reviews-panel">
      {/* Header — titolo sezione (DENTRO la card) + metriche hero + actions.
       *  Padding allineato a quelli usati nelle altre card della pagina
       *  (`p-5` come il KPI grid, divider sottile slate-100 fra header e
       *  lista). */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500" data-testid="google-reviews-title">
            Recensioni Google
          </h3>
          <div className="flex items-center gap-2">
            {canRefresh && (
              <button
                type="button"
                onClick={triggerRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-slate-700 hover:text-black border border-slate-300 hover:border-black px-3 py-2 disabled:opacity-50 transition-colors"
                data-testid="google-reviews-refresh"
              >
                <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
                Aggiorna
              </button>
            )}
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-slate-700 hover:text-black border border-slate-300 hover:border-black px-3 py-2 transition-colors"
              data-testid="google-reviews-maps-link"
            >
              <ExternalLink className="w-3 h-3" />
              Maps
            </a>
          </div>
        </div>
        <div className="flex items-end gap-10 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Rating medio</p>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="font-heading text-2xl md:text-4xl font-bold tabular-nums text-slate-900" data-testid="google-rating">
                {rating != null ? rating.toFixed(1) : '—'}
              </span>
              <Star className="w-4 h-4 text-amber-500 fill-amber-500 ml-1" />
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Recensioni totali</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-heading text-2xl md:text-4xl font-bold tabular-nums text-slate-900" data-testid="google-review-count">
                {reviewCount != null ? reviewCount.toLocaleString('it-IT') : '—'}
              </span>
              {stale && (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-600">cache</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews list */}
      {reviews.length > 0 ? (
        <div className="divide-y divide-slate-100" data-testid="google-reviews-list">
          {reviews.map((r, i) => {
            const author = (r.authorAttribution || {}).displayName || 'Utente Google';
            const stars = r.rating || 0;
            const text = (r.text || {}).text || '';
            const when = r.relativePublishTimeDescription || '';
            return (
              <article key={r.name || i} className="px-5 py-4" data-testid={`google-review-${i}`}>
                <header className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-900 truncate">{author}</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 whitespace-nowrap">{when}</span>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star
                        key={n}
                        className={`w-3.5 h-3.5 ${n <= stars ? 'text-amber-500 fill-amber-500' : 'text-slate-200 fill-slate-200'}`}
                      />
                    ))}
                  </div>
                </header>
                {text && (
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3" data-testid={`google-review-text-${i}`}>
                    {text}
                  </p>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="p-5" data-testid="google-reviews-empty">
          <p className="text-sm text-slate-500">
            Nessuna recensione testuale disponibile per questo negozio.
          </p>
          {displayName && (
            <p className="text-xs text-slate-400 mt-1">
              Leggi tutte le recensioni su{' '}
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">Google Maps</a>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
